import {
  AccountInitialData,
  AccountMeta,
  AccountSignUpPayload,
  AccountSignUpResponse,
  AccountSignUpThroughGooglePayload,
  IsDomainExistsPayload,
  IsDomainExistsResponse,
} from 'module/api/account/types';
import { CurrencyMappingList } from 'module/api/account/types';
import {
  CustomerCompany,
  UpdateCustomerCompanyPayload,
} from 'module/api/company/types';
import { UserProfileInfo, UserMeta } from 'module/api/user/types';
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { AccountAPI } from 'api/account';
import { CompanyAPI } from 'api/company';
import { oneDay } from 'queries/constants';

const ACCOUNT_META_QUERY_KEY = ['account-meta'];

export const CURRENCY_MAPPING_LIST_KEY = ['CURRENCY_MAPPING_LIST'];

// useQuery({ queryKey: ['todos'], queryFn: fetchTodoList })

export const useAccountMetaQuery = (
  queryOptions?: UseQueryOptions<
    AccountMeta,
    unknown,
    AccountMeta,
    typeof ACCOUNT_META_QUERY_KEY
  >,
): UseQueryResult<AccountMeta, unknown> => {
  return useQuery({
    ...(queryOptions ?? {}),
    queryKey: ACCOUNT_META_QUERY_KEY,
    queryFn: () => AccountAPI.getMeta(),
    staleTime: oneDay,
  });
};
export const useAccountSignUpMutation = (): UseMutationResult<
  AccountSignUpResponse,
  unknown,
  AccountSignUpPayload
> => {
  return useMutation({
    mutationFn: (payload) => AccountAPI.signUp(payload),
  });
};

export const useSignUpThroughGoogle = (): UseMutationResult<
  AccountSignUpResponse,
  unknown,
  AccountSignUpThroughGooglePayload
> => {
  return useMutation({
    mutationFn: (payload: AccountSignUpThroughGooglePayload) =>
      AccountAPI.signUpThroughGoogle(payload),
  });
};

export const useIsDomainExistsMutation = (): UseMutationResult<
  IsDomainExistsResponse,
  unknown,
  IsDomainExistsPayload
> => {
  return useMutation({
    mutationFn: (payload) => AccountAPI.isDomainExists(payload),
  });
};

export const useCurrencyMappingListQuery = (
  queryOptions: Partial<
    UseQueryOptions<
      CurrencyMappingList,
      Error,
      CurrencyMappingList,
      typeof CURRENCY_MAPPING_LIST_KEY
    >
  > = {},
): UseQueryResult<CurrencyMappingList, Error> =>
  useQuery({
    queryKey: CURRENCY_MAPPING_LIST_KEY,
    queryFn: () => AccountAPI.getCurrencyMappingList(),
    ...queryOptions,
  });

type UpdateCustomerCompanyVariable = {
  companyId: number;
  payload: UpdateCustomerCompanyPayload;
};
export const useUpdateCustomerCompanyMutation = (
  options: UseMutationOptions<
    CustomerCompany,
    unknown,
    UpdateCustomerCompanyVariable
  > = {},
) => {
  return useMutation({
    mutationFn: ({ companyId, payload }: UpdateCustomerCompanyVariable) =>
      CompanyAPI.updateCompany(companyId, payload),
    ...options,
  });
};
const ACCOUNT_INITIAL_DATA_QUERY_KEY = ['ACCOUNT_INITIAL_DATA'];

export const useAccountInitialDataQuery = <TData = AccountInitialData>(
  queryOptions: Omit<
    UseQueryOptions<
      AccountInitialData,
      Error,
      TData,
      typeof ACCOUNT_INITIAL_DATA_QUERY_KEY
    >,
    'queryKey'
  > = {},
) => {
  return useQuery({
    queryKey: ACCOUNT_INITIAL_DATA_QUERY_KEY,
    queryFn: () => AccountAPI.getAccountInitData(),
    ...queryOptions,
  });
};

export const useCurrentUserQuery = (
  queryOptions: Omit<
    UseQueryOptions<
      AccountInitialData,
      Error,
      UserProfileInfo,
      typeof ACCOUNT_INITIAL_DATA_QUERY_KEY
    >,
    'queryKey'
  > = {},
) => {
  return useAccountInitialDataQuery({
    select: (data) => data.userProfile,
    ...queryOptions,
  });
};

export const useUserMetaQuery = (
  queryOptions: Omit<
    UseQueryOptions<
      AccountInitialData,
      Error,
      UserMeta,
      typeof ACCOUNT_INITIAL_DATA_QUERY_KEY
    >,
    'queryKey'
  > = {},
) => {
  return useAccountInitialDataQuery({
    select: (data) => data.userProfile.userMeta,
    ...queryOptions,
  });
};
